import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HardHat, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useSort, SortHead } from "../components/SortHead";
import api, { fmtNum, fmtDate } from "../lib/api";

const ALL = "all";
const yearOptions = () => {
  const y = new Date().getFullYear();
  return [y - 2, y - 1, y, y + 1];
};

export default function WorkersPage() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [scope, setScope] = useState("year");
  const [data, setData] = useState(null);
  const [newName, setNewName] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = scope === "year" ? { year } : {};
    api.get("/workers", { params }).then((r) => setData(r.data));
  };

  useEffect(load, [year, scope]);

  const wAccessors = {
    name: (w) => w.name.toLowerCase(),
    count: (w) => w.count || 0,
    total: (w) => w.total_eur || 0,
    bam: (w) => w.total_bam || 0,
    last: (w) => w.last_date || "",
  };
  const { sorted: sortedWorkers, sort, toggle } = useSort(data?.workers || [], wAccessors);

  const addWorker = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/workers", { name: newName });
      toast.success(`Radnik ${newName} dodan`);
      setNewName("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška");
    }
  };

  const removeWorker = async (w) => {
    if (!window.confirm(`Ukloniti radnika ${w.name} s popisa? (transakcije ostaju)`)) return;
    await api.delete(`/workers/${w.id}`);
    toast.success("Radnik uklonjen");
    load();
  };

  const openDetail = (w) => {
    const params = scope === "year" ? { year } : {};
    api.get(`/workers/${w.id}/transactions`, { params }).then((r) => setDetail({ ...w, ...r.data }));
  };

  return (
    <div className="space-y-6 fade-up" data-testid="workers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Isplate plaća po osobi</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Radnici</h1>
          <p className="text-xs text-[#64748B] mt-1" data-testid="workers-count">{(data?.workers || []).length} radnika na popisu</p>
        </div>
        <div className="flex gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="workers-scope-select" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="year">Odabrana godina</SelectItem>
              <SelectItem value={ALL}>Sve vrijeme</SelectItem>
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="workers-year-select" className="w-28 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}.</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5 md:col-span-2">
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-1">Ukupno isplaćeno radnicima {scope === "year" ? `(${year}.)` : "(sve vrijeme)"}</p>
          <p data-testid="workers-total" className="font-heading text-3xl font-bold text-[#DC2626] tabular-nums">−{fmtNum(data?.total_eur || 0, "€")}</p>
          <p className="text-xs text-[#64748B] mt-1">Isplate se prepoznaju automatski po imenu radnika u primatelju (npr. "Toni Ćorić Ugovor o radu"), neovisno o kategoriji. Pri uvozu izvoda kategorija se automatski postavlja na "Radnici".</p>
        </div>
        <form onSubmit={addWorker} className="bg-white border border-[#E2E8F0] rounded-sm p-5 space-y-2">
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">Dodaj radnika</p>
          <div className="flex gap-2">
            <Input data-testid="new-worker-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ime i prezime..." />
            <Button data-testid="add-worker-btn" type="submit" className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm shrink-0">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>

      {data?.salary_check?.missing?.length > 0 && (
        <div
          data-testid="salary-warning-banner"
          className={`border rounded-sm px-4 py-3 text-sm ${data.salary_check.deadline_passed ? "bg-[#DC2626]/10 border-[#DC2626]/40 text-[#7A3229]" : "bg-[#D97706]/10 border-[#D97706]/40 text-[#0F172A]"}`}
        >
          <span className="font-semibold">
            Plaća za {data.salary_check.prev_month} nije evidentirana za {data.salary_check.missing.length} {data.salary_check.missing.length === 1 ? "radnika" : "radnika"}:
          </span>{" "}
          {data.salary_check.missing.join(", ")}.{" "}
          {data.salary_check.deadline_passed
            ? "Rok (15. u mjesecu) je prošao — spojite isplatu s izvodom (Uvoz izvoda) ili je povežite ručno (Transakcije → Uredi → Radnik)."
            : `Rok je ${data.salary_check.deadline} — ako je plaća isplaćena, spojite je s izvodom ili povežite ručno.`}
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
              <TableHead className="w-10 text-center">#</TableHead>
              <SortHead label="Radnik" sortKey="name" sort={sort} onToggle={toggle} />
              <SortHead label="Broj isplata" sortKey="count" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <SortHead label="Ukupno EUR" sortKey="total" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <SortHead label="od toga BAM" sortKey="bam" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <SortHead label="Zadnja isplata" sortKey="last" sort={sort} onToggle={toggle} className="text-right" align="right" />
              <TableHead className="w-14"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="workers-table-body">
            {sortedWorkers.map((w, i) => (
              <TableRow key={w.id} data-testid={`worker-row-${w.name}`} onClick={() => w.count > 0 && openDetail(w)} className={`transition-colors duration-200 ${w.count > 0 ? "cursor-pointer hover:bg-[#F8FAFC]" : "opacity-60"}`}>
                <TableCell data-testid={`worker-num-${i}`} className="text-center text-xs text-[#64748B] tabular-nums font-medium">{i + 1}.</TableCell>
                <TableCell className="font-semibold text-sm">
                  <span className="flex items-center gap-2 flex-wrap">
                    <HardHat className="w-3.5 h-3.5 text-[#64748B]" /> {w.name}
                    {w.salary_paid_this_month === false && (
                      <span
                        data-testid={`salary-missing-${w.name}`}
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${data?.salary_check?.deadline_passed ? "bg-[#DC2626]/15 text-[#DC2626]" : "bg-[#D97706]/15 text-[#8A6420]"}`}
                      >
                        nema plaće za {data?.salary_check?.prev_month?.split(" ")[0] || "prošli mjesec"}
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm">{w.count || "—"}</TableCell>
                <TableCell className="text-right tabular-nums text-sm font-semibold text-[#DC2626]">{w.total_eur ? `−${fmtNum(w.total_eur)}` : "—"}</TableCell>
                <TableCell className="text-right tabular-nums text-xs text-[#475569]">{w.total_bam ? fmtNum(w.total_bam, "KM") : "—"}</TableCell>
                <TableCell className="text-right text-sm text-[#475569]">{w.last_date ? fmtDate(w.last_date) : "—"}</TableCell>
                <TableCell>
                  <button data-testid={`delete-worker-${w.name}`} onClick={(e) => { e.stopPropagation(); removeWorker(w); }} className="p-1.5 text-[#64748B] hover:text-[#DC2626] transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="worker-detail-dialog" className="max-w-lg rounded-sm max-h-[80vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg text-[#0F172A] flex items-center gap-2">
                  <HardHat className="w-5 h-5 text-[#0D9488]" /> {detail.name} — isplate
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-1.5">
                {detail.transactions.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 border border-[#E2E8F0] rounded-sm px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm text-[#0F172A] truncate">{t.counterparty}</p>
                      <p className="text-xs text-[#475569]">{fmtDate(t.date)}{t.recipient_account ? ` · ${t.recipient_account}` : ""}</p>
                    </div>
                    <p className="tabular-nums font-semibold text-sm text-[#DC2626] shrink-0">−{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
